import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import UserHeader from "@/components/user/UserHeader";
import Footer from "@/components/layout/Footer";
import { Home, KeyRound, User, ArrowRight } from 'lucide-react';
import { toast } from "@/components/ui/sonner";

const ProponenteLogin: React.FC = () => {
  const navigate = useNavigate();
  
  // Estados do formulário
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [showConfirmPassword, setShowConfirmPassword] = useState(true); // Para usuários novos
  const [documento, setDocumento] = useState('');
  const [tipoDocumento, setTipoDocumento] = useState('cpf');
  const [lembrarUsuario, setLembrarUsuario] = useState(false);
  
  // Função para formatar CPF e CNPJ
  const formatarDocumento = (valor: string): string => {
    // Remove caracteres não numéricos
    const apenasNumeros = valor.replace(/\D/g, '');
    
    if (tipoDocumento === 'cpf') {
      // Formata como CPF: 123.456.789-01
      return apenasNumeros
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
        .substring(0, 14);
    } else {
      // Formata como CNPJ: 12.345.678/0001-90
      return apenasNumeros
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2')
        .substring(0, 18);
    }
  };
  
  // Função para validar formulário
  const validarFormulario = (): boolean => {
    if (!documento) {
      toast.error('Digite seu CPF ou CNPJ');
      return false;
    }
    
    if (tipoDocumento === 'cpf' && documento.length !== 14) {
      toast.error('CPF inválido');
      return false;
    }
    
    if (tipoDocumento === 'cnpj' && documento.length !== 18) {
      toast.error('CNPJ inválido');
      return false;
    }
    
    if (!email) {
      toast.error('Digite seu e-mail');
      return false;
    }
    
    if (!senha) {
      toast.error('Digite sua senha');
      return false;
    }
    
    if (showConfirmPassword) {
      if (!confirmarSenha) {
        toast.error('Confirme sua senha');
        return false;
      }
      
      if (senha !== confirmarSenha) {
        toast.error('As senhas não coincidem');
        return false;
      }
      
      if (senha.length < 8) {
        toast.error('A senha deve ter pelo menos 8 caracteres');
        return false;
      }
    }
    
    return true;
  };
  
  // Função para lidar com o envio do formulário
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validarFormulario()) return;
    
    if (showConfirmPassword) {
      // Lógica para criar conta
      toast.success('Conta criada com sucesso!');
      setTimeout(() => {
        navigate('/pregao-dashboard');
      }, 1500);
    } else {
      // Lógica para fazer login
      toast.success('Login realizado com sucesso!');
      setTimeout(() => {
        navigate('/pregao-dashboard');
      }, 1500);
    }
  };
  
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <UserHeader />
      
      {/* Título e botões de ação */}
      <div className="bg-white px-6 py-4 border-b border-gray-200">
        <div className="container mx-auto flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-800">Acesso ao Sistema de Pregão Eletrônico</h1>
          <div className="flex space-x-2">
            <Button 
              className="bg-[#0e7cb4] hover:bg-[#0a5c87] text-white"
              onClick={() => navigate('/dashboard')}
            >
              <Home className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
      
      {/* Conteúdo principal */}
      <div className="flex-grow p-6 bg-gray-50">
        <div className="container mx-auto max-w-md">
          <Card>
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold mb-6 text-center">
                {showConfirmPassword ? 'Criar nova conta' : 'Login de Proponente'}
              </h2>
              
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <div className="flex space-x-4 mb-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="cpf"
                        checked={tipoDocumento === 'cpf'}
                        onChange={() => {
                          setTipoDocumento('cpf');
                          setDocumento('');
                        }}
                        className="mr-2"
                      />
                      CPF
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="cnpj"
                        checked={tipoDocumento === 'cnpj'}
                        onChange={() => {
                          setTipoDocumento('cnpj');
                          setDocumento('');
                        }}
                        className="mr-2"
                      />
                      CNPJ
                    </label>
                  </div>
                  
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={documento}
                      onChange={(e) => setDocumento(formatarDocumento(e.target.value))}
                      className="w-full pl-10 pr-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={tipoDocumento === 'cpf' ? 'Digite seu CPF' : 'Digite seu CNPJ'}
                      maxLength={tipoDocumento === 'cpf' ? 14 : 18}
                    />
                  </div>
                </div>
                
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2">E-mail</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Digite seu e-mail"
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2">Senha</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <KeyRound className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="password"
                      value={senha}
                      onChange={(e) => setSenha(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Digite sua senha"
                    />
                  </div>
                </div>
                
                {showConfirmPassword && (
                  <div className="mb-4">
                    <label className="block text-gray-700 mb-2">Confirmar Senha</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <KeyRound className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="password"
                        value={confirmarSenha}
                        onChange={(e) => setConfirmarSenha(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Confirme sua senha"
                      />
                    </div>
                  </div>
                )}
                
                {!showConfirmPassword && (
                  <div className="flex items-center mb-4">
                    <input
                      id="lembrar"
                      type="checkbox"
                      checked={lembrarUsuario}
                      onChange={(e) => setLembrarUsuario(e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <label htmlFor="lembrar" className="ml-2 text-sm text-gray-700">
                      Lembrar meu usuário
                    </label>
                  </div>
                )}
                
                <div className="mt-6">
                  <Button 
                    type="submit"
                    className="w-full bg-[#0e7cb4] hover:bg-[#0a5c87] text-white py-2 flex items-center justify-center"
                  >
                    {showConfirmPassword ? 'Criar Conta' : 'Entrar'}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
                
                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="text-[#0e7cb4] hover:underline text-sm"
                  >
                    {showConfirmPassword ? 'Já tem uma conta? Faça login' : 'Não tem uma conta? Crie agora'}
                  </button>
                </div>
                
                {!showConfirmPassword && (
                  <div className="mt-2 text-center">
                    <button
                      type="button"
                      className="text-gray-600 hover:underline text-sm"
                    >
                      Esqueceu sua senha?
                    </button>
                  </div>
                )}
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Rodapé */}
      <Footer />
    </div>
  );
};

export default ProponenteLogin; 